import React from 'react';
import { Link } from 'react-router-dom';
import Hero from '@/components/Hero';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Lightbulb, Heart, Infinity, ArrowRight, HelpCircle, GraduationCap, Calendar, MapPin, Users } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import OptimizedImage from '@/components/OptimizedImage';
import GrainTexture from '@/components/GrainTexture';
import yinyangbruin from '@/assets/yinyangbruin.png';
import yinyangbruinWebp from '@/assets/yinyangbruin.webp';
import yinyangpaars from '@/assets/yinyangpaars.png';
import yinyangpaarsWebp from '@/assets/yinyangpaars.webp';
import { SEO } from '@/components/SEO';
import { ReviewSection } from '@/components/ReviewSection';
import { familieopstellingReviews } from '@/data/reviews';
import { EmailLink } from '@/components/EmailLink';
import { getSiteLastUpdatedDateTimeISO } from '@/constants/siteLastUpdated';
import reviewsVisual from '@/assets/webp bestanden/221 - TwentyTwelvePhotography_Systemique.webp';

const OFFER_PRICE_VALID_UNTIL = '2026-12-31';

const homepageServiceOffers = [
  {
    name: 'Leergang systemisch leiderschap',
    description: 'Duiken in de Onderstroom — compacte 3-daagse leergang voor leiders en adviseurs',
    url: 'https://systemique.nl/leergang-systemisch-leiderschap',
    price: '1500.00',
    priceCurrency: 'EUR',
  },
  {
    name: 'Familieopstellingen',
    description: 'Familieopstellingen voor particulieren in Noord-Holland',
    url: 'https://systemique.nl/familieopstellingen',
    price: '100.00',
    priceCurrency: 'EUR',
  },
  {
    name: 'Organisatieopstellingen',
    description: 'Organisatieopstellingen voor teams en bedrijven',
    url: 'https://systemique.nl/organisatieopstellingen',
    priceSpecification: {
      '@type': 'PriceSpecification',
      priceCurrency: 'EUR',
      description: 'Maatwerk — offerte op aanvraag',
    },
  },
  {
    name: 'Teamcoaching',
    description: 'Systemische teamcoaching voor organisaties',
    url: 'https://systemique.nl/teamcoaching',
    priceSpecification: {
      '@type': 'PriceSpecification',
      priceCurrency: 'EUR',
      description: 'Maatwerk — offerte op aanvraag',
    },
  },
] as const;

const Index = () => {
  return (
    <>
      <SEO 
        title="Organisatieopstellingen, familieopstellingen & coaching | Systemique"
        description="Systemique: organisatieopstellingen en teamcoaching voor teams; familieopstellingen en 1-op-1 in Castricum, Amsterdam en Noord-Holland."
        keywords="systemique Castricum, systemische coaching Castricum, systemische coach Amsterdam, familieopstellingen Castricum, familieopstellingen Amsterdam, familieopstellingen Uitgeest, systemisch werk Haarlem, familieopstellingen Zaanstreek, systemische coaching Noord-Holland, systemisch werk Alkmaar, organisatieopstellingen Amsterdam, systemische coach Zaandam, teamcoaching systemisch, systemisch werk Nederland"
        canonical="https://systemique.nl/"
      />
      
      {/* LocalBusiness Schema met Reviews */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ProfessionalService",
            "@id": "https://systemique.nl",
            "name": "Systemique - Systemische Coaching & Familieopstellingen",
            "alternateName": ["Systemique Marthe van der Molen", "Systemique Castricum"],
            "image": "https://systemique.nl/OG-image.jpg",
            "description": "Systemische coaching, familieopstellingen en organisatieopstellingen in Castricum, Amsterdam, Haarlem en omgeving",
            "priceRange": "€€",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Castricum",
              "addressRegion": "Noord-Holland",
              "addressCountry": "NL"
            },
            "telephone": "+31-6-12991453",
            "url": "https://systemique.nl",
            "areaServed": [
              {"@type": "City", "name": "Castricum"},
              {"@type": "City", "name": "Amsterdam"},
              {"@type": "City", "name": "Haarlem"},
              {"@type": "City", "name": "Zaandam"},
              {"@type": "City", "name": "Alkmaar"},
              {"@type": "City", "name": "Uitgeest"},
              {"@type": "City", "name": "Heerhugowaard"},
              {"@type": "City", "name": "Hoorn"},
              {"@type": "City", "name": "Beverwijk"},
              {"@type": "City", "name": "Hoofddorp"},
              {"@type": "City", "name": "Amstelveen"},
              {"@type": "AdministrativeArea", "name": "Noord-Holland"}
            ],
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "5.0",
              "bestRating": "5",
              "ratingCount": "3",
              "reviewCount": "3"
            },
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Systemische diensten",
              "itemListElement": homepageServiceOffers.map((service) => ({
                "@type": "Offer",
                "priceCurrency": "EUR",
                "availability": "https://schema.org/InStock",
                "url": service.url,
                "priceValidUntil": OFFER_PRICE_VALID_UNTIL,
                ...(service.price
                  ? { price: service.price }
                  : { priceSpecification: service.priceSpecification }),
                "itemOffered": {
                  "@type": "Service",
                  "name": service.name,
                  "description": service.description,
                },
              }))
            }
          })
        }}
      />
      
      {/* FAQPage Schema voor Google Rich Results */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Wat is systemisch werk?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Systemisch werk kijkt naar de onderstroom: de onzichtbare patronen en dynamieken in families, teams of organisaties. Door middel van opstellingen met representanten of symbolen maken we zichtbaar waar gedrag en problemen vandaan komen. Niet alleen de symptomen, maar de bron. Dit geeft vaak razendsnel inzicht en creëert ruimte voor duurzame verandering."
                }
              },
              {
                "@type": "Question",
                "name": "Wat kost een familieopstelling?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "€100 als vraaginbrenger per opstellingenmiddag. Als representant €75 per los ticket, of een strippenkaart voor korting bij meerdere sessies."
                }
              },
              {
                "@type": "Question",
                "name": "Wat kost een organisatieopstelling?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Dat is maatwerk. Op basis van jouw vraagstuk ontvang je een passende offerte. Neem contact op via 06-12991453 of marthe@systemique.nl voor een vrijblijvend gesprek."
                }
              },
              {
                "@type": "Question",
                "name": "Waar vinden de opstellingenmiddagen plaats?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Dat verschilt: van Castricum tot Amsterdam en ook op jouw locatie indien gewenst. Groepsopstellingen vinden vaak plaats in Het Atelier in Uitgeest. Bekijk de agenda voor specifieke locaties."
                }
              },
              {
                "@type": "Question",
                "name": "Wat kan ik verwachten bij mijn eerste opstelling?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Elke opstelling is anders. Ik zorg ervoor dat jij regie hebt op het proces en dat je in een veilige setting bent. Je bepaalt zelf hoe diep je gaat en wat je deelt."
                }
              },
              {
                "@type": "Question",
                "name": "Moet ik mijn verhaal delen met de hele groep?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Nee. Als je in een groep komt, vraag ik kort uit wat het vraagstuk is. Je hoeft geen gedetailleerd verhaal uit de doeken te doen. Mocht je dit bezwaarlijk vinden, dan kun je ook een 1-op-1 sessie boeken."
                }
              },
              {
                "@type": "Question",
                "name": "Kan ik ook als representant meedoen zonder eigen vraag?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Ja, er is altijd plek voor representanten. Ik bied ook strippenkaarten aan voor representanten, dat is voordelig als je vaker wilt komen representeren."
                }
              },
              {
                "@type": "Question",
                "name": "Hoe boek ik een kennismakingsgesprek?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Dat kan altijd. Bel me op 06-12991453 of stuur een bericht via het contactformulier op systemique.nl/contact"
                }
              }
            ]
          })
        }}
      />
      
      {/* Article Schema voor AI-zoekmachines */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "Wat is systemisch werk? Uitleg en toepassingen",
            "description": "Systemisch werk is een methodiek die kijkt naar de onderstroom in systemen. Uitleg over familieopstellingen, organisatieopstellingen en systemische coaching.",
            "image": "https://systemique.nl/OG-image.jpg",
            "author": {
              "@type": "Person",
              "name": "Marthe van der Molen",
              "jobTitle": "Systemisch organisatiecoach en trainer",
              "url": "https://systemique.nl/over",
              "sameAs": [
                "https://www.linkedin.com/in/marthe-van-der-molen",
                "https://www.instagram.com/systemique.nl"
              ]
            },
            "publisher": {
              "@type": "Organization",
              "name": "Systemique",
              "url": "https://systemique.nl",
              "sameAs": [
                "https://www.linkedin.com/in/marthe-van-der-molen",
                "https://www.instagram.com/systemique.nl"
              ],
              "logo": {
                "@type": "ImageObject",
                "url": "https://systemique.nl/logo.png",
                "contentUrl": "https://systemique.nl/logo.png",
                "width": 120,
                "height": 40
              }
            },
            "datePublished": "2024-01-01",
            "dateModified": getSiteLastUpdatedDateTimeISO(),
            "mainEntityOfPage": "https://systemique.nl",
            "keywords": "systemisch werk, systemische coaching, familieopstellingen, organisatieopstellingen, systemisch perspectief, onderstroom, systemische dynamieken, systemisch leiderschap",
            "articleBody": "Systemisch werk is een methodiek die kijkt naar de onderstroom en dynamieken in systemen zoals families, teams en organisaties. Het maakt onzichtbare patronen, loyaliteiten en verstrikkingen zichtbaar. Systemisch werk helpt om de bron van gedrag en problemen te vinden, niet alleen de symptomen. Bij Systemique bieden we systemische coaching, familieopstellingen en organisatieopstellingen aan in Castricum, Amsterdam en Haarlem. We werken met leiders, teams en particulieren die patronen willen doorbreken of inzicht willen in systemische dynamieken."
          })
        }}
      />
      
      {/* HowTo Schema voor AI-zoekmachines */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            "name": "Hoe werkt systemisch werk?",
            "description": "Stap-voor-stap uitleg van hoe systemisch werk werkt en wat je kunt verwachten bij Systemique",
            "step": [
              {
                "@type": "HowToStep",
                "name": "Kennismaking en intake",
                "text": "Je neemt contact op met Systemique voor een kennismakingsgesprek. We bespreken jouw vraag of uitdaging en bepalen welke vorm van systemisch werk het beste past: familieopstellingen, organisatieopstellingen, teamcoaching of een opleiding. Contact: marthe@systemique.nl of 06-12991453."
              },
              {
                "@type": "HowToStep",
                "name": "Deelname aan opstelling of coaching",
                "text": "Je neemt deel aan een opstellingenmiddag in Het Atelier, Uitgeest, 1-op-1 coaching in Castricum, of teamcoaching op locatie. Tijdens de sessie worden systemische dynamieken zichtbaar gemaakt door representanten of symbolen te gebruiken."
              },
              {
                "@type": "HowToStep",
                "name": "Inzicht en integratie",
                "text": "Je krijgt inzicht in patronen en dynamieken in jouw systeem. Deze inzichten kun je direct toepassen in je dagelijks leven of werk. Systemisch werk geeft vaak razendsnel inzicht en creëert ruimte voor duurzame verandering."
              }
            ],
            "totalTime": "PT3H",
            "supply": [
              {
                "@type": "HowToSupply",
                "name": "Strippenkaart of los ticket voor opstellingenmiddagen"
              }
            ],
            "tool": [
              {
                "@type": "HowToTool",
                "name": "Systemische opstellingen"
              }
            ]
          })
        }}
      />
      
      {/* DefinedTermSet Schema voor AI-zoekmachines */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "DefinedTermSet",
            "name": "Systemisch Werk Terminologie",
            "description": "Uitleg van termen en begrippen gerelateerd aan systemisch werk",
            "hasDefinedTerm": [
              {
                "@type": "DefinedTerm",
                "name": "Systemisch werk",
                "description": "Een methodiek die kijkt naar de onderstroom en dynamieken in systemen zoals families, teams en organisaties. Het maakt onzichtbare patronen, loyaliteiten en verstrikkingen zichtbaar.",
                "inDefinedTermSet": "https://systemique.nl"
              },
              {
                "@type": "DefinedTerm",
                "name": "Familieopstelling",
                "description": "Een systemische methode waarbij familiepatronen en dynamieken zichtbaar worden gemaakt door representanten of symbolen te gebruiken. Helpt bij persoonlijke ontwikkeling en inzicht in familiepatronen.",
                "inDefinedTermSet": "https://systemique.nl"
              },
              {
                "@type": "DefinedTerm",
                "name": "Organisatieopstelling",
                "description": "Een systemische methode die dynamieken in teams en organisaties zichtbaar maakt, zoals hoog verloop, conflicten of samenwerkingsproblemen. Helpt bij organisatieontwikkeling.",
                "inDefinedTermSet": "https://systemique.nl"
              },
              {
                "@type": "DefinedTerm",
                "name": "Onderstroom",
                "description": "De onzichtbare dynamieken, patronen en krachten in een systeem die gedrag en resultaten beïnvloeden. Systemisch werk maakt de onderstroom zichtbaar.",
                "inDefinedTermSet": "https://systemique.nl"
              },
              {
                "@type": "DefinedTerm",
                "name": "Systemische coaching",
                "description": "Coaching die gebruik maakt van systemisch werk om individuen, teams en organisaties te begeleiden. Helpt bij het herkennen en doorbreken van systemische patronen.",
                "inDefinedTermSet": "https://systemique.nl"
              },
              {
                "@type": "DefinedTerm",
                "name": "Representant",
                "description": "Iemand die tijdens een opstelling een rol inneemt (bijvoorbeeld een familielid of teamlid) om dynamieken zichtbaar te maken.",
                "inDefinedTermSet": "https://systemique.nl"
              }
            ]
          })
        }}
      />
      
      <Hero />
      
      {/* Intro Cards */}
      <section id="wat-brengt-je-hier" className="py-24 px-8 md:px-6 bg-[#854C73] text-white overflow-hidden relative">
        <GrainTexture opacity={0.15} />
        {/* Rotating circle diagram with gradient blur */}
        <div className="absolute top-1/2 right-0 -translate-y-1/2 w-96 h-96 opacity-20">
          <div className="w-full h-full rounded-full border-2 border-white/30 animate-spin-slow relative">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full bg-gradient-to-tr from-[#F9EBEF]/20 via-[#854C73]/10 to-transparent blur-3xl"></div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="mb-20">
            {/* Large left-aligned heading */}
            <h2 className="font-serif text-4xl md:text-6xl mb-6 leading-tight">
              Wat brengt je <span className="italic font-light font-serif text-[#F9EBEF] opacity-80">hier</span>
            </h2>
            {/* Body text - left aligned, white */}
            <div className="max-w-3xl">
              <p className="text-white text-lg md:text-xl leading-relaxed" style={{ fontFamily: "'Noto Serif Display', serif" }}>
                Hieronder vind je een selectie van mijn dienstverlening voor organisaties, teams en individuen
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                title: 'Organisatieopstellingen',
                desc: 'Maak organisatiedynamieken zichtbaar en creëer ruimte voor nieuwe mogelijkheden.',
                link: '/organisatieopstellingen',
                alt: 'Organisatieopstellingen - Systemisch werk voor teams en organisaties',
                useBrownIcon: false,
              },
              {
                title: 'Teamcoaching',
                desc: 'Versterk de teamsamenwerking door patronen in de onderstroom zichtbaar te maken.',
                link: '/teamcoaching',
                alt: 'Teamcoaching - Systemische teambegeleiding bij Systemique',
                useBrownIcon: false,
              },
              {
                title: 'Familieopstellingen',
                desc: 'Maak onzichtbare dynamieken zichtbaar en creëer ruimte voor nieuwe mogelijkheden.',
                link: '/familieopstellingen',
                alt: 'Familieopstellingen - Systemisch werk voor persoonlijke ontwikkeling',
                useBrownIcon: true,
              },
              {
                title: 'Aanbod',
                desc: 'Bekijk alle diensten van Systemique op één plek.',
                link: '/aanbod',
                alt: 'Aanbod Systemique - Overzicht van diensten en trajecten',
                useBrownIcon: true,
              },
            ].map((card) => (
              <Link
                key={card.link}
                to={card.link}
                className="min-w-0 break-words bg-[#F9EBEF] text-[#854C73] p-6 md:p-10 rounded-2xl hover:-translate-y-2 transition-transform duration-300 shadow-xl cursor-pointer group animate-pulse-border relative overflow-visible"
              >
                <OptimizedImage
                  src={card.useBrownIcon ? yinyangbruin : yinyangpaars}
                  webpSrc={card.useBrownIcon ? yinyangbruinWebp : yinyangpaarsWebp}
                  alt={card.alt}
                  className="w-12 h-12 mb-6 opacity-80 group-hover:opacity-100 transition-opacity"
                  width={48}
                  height={48}
                />
                <h3 className="font-serif text-2xl font-bold mb-4 group-hover:text-[#854C73] transition-colors">{card.title}</h3>
                <p className="text-sm leading-relaxed opacity-80 mb-8 font-light">{card.desc}</p>
                <div className="flex items-center text-sm font-bold uppercase tracking-wider border-b border-[#854C73]/20 pb-1 w-fit group-hover:border-[#854C73] transition-colors text-[#854C73]">
                  Ontdek meer <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Wat is systemisch werk? - Met visuele elementen */}
      <section className="py-12 md:py-24 bg-[#FAEBEE] px-6 relative">
        <GrainTexture opacity={0.1} />
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="grid md:grid-cols-2 gap-6 md:gap-12 items-center">
            {/* Linker kolom: Tekst */}
            <div>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#854C73] mb-4 md:mb-6">
                Wat is <span className="italic font-light font-serif">systemisch werk</span>
              </h2>
              
              <div className="space-y-4 md:space-y-6 text-[#6b4912]">
                <p className="text-base md:text-lg leading-relaxed">
                  <strong>Systemisch werk</strong> is een methodiek die kijkt naar de <strong>onderstroom</strong> en dynamieken in systemen zoals families, teams en organisaties. Het maakt onzichtbare patronen, loyaliteiten en verstrikkingen zichtbaar.
                </p>
                
                <p className="text-base md:text-lg leading-relaxed">
                  In plaats van alleen naar symptomen te kijken, helpt systemisch werk om de <strong>bron</strong> van gedrag en problemen te vinden. Dit gebeurt door middel van <strong>opstellingen</strong> waarbij representanten of symbolen de dynamieken in een systeem zichtbaar maken.
                </p>
                
                <p className="text-base md:text-lg leading-relaxed">
                  Systemique biedt <strong>systemische coaching</strong>, <strong>familieopstellingen</strong> en <strong>organisatieopstellingen</strong> aan in Castricum, Amsterdam en Haarlem. Lees meer over{' '}
                  <Link to="/over" className="text-[#854C73] underline hover:text-[#590D0D] transition-colors">
                    systemisch coach in Noord-Holland
                  </Link>
                  .
                </p>
                
                <div className="pt-4">
                  <Button asChild variant="default" size="lg" className="bg-[#854C73] hover:bg-[#590D0D] w-full md:w-auto">
                    <Link to="/aanbod">Bekijk het aanbod</Link>
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Rechter kolom: Visueel element met bestaande voordelen */}
            <div className="relative mt-8 md:mt-0">
              <div className="bg-white rounded-2xl p-6 md:p-8 shadow-xl border border-[#FAEBEE]">
                <div className="mb-4 md:mb-6">
                  <Badge className="mb-2">Waarom systemisch?</Badge>
                  <h3 className="font-serif text-xl md:text-2xl text-[#854C73]">De bron van jouw gedrag</h3>
          </div>
                
                <div className="space-y-4">
            {[
              { title: "Razendsnel Inzicht", desc: "Ontdek binnen korte tijd waar patronen vandaan komen en wat de oplossing is.", icon: Lightbulb },
              { title: "Lichamelijke Verankering", desc: "Oplossingen die niet rationeel bedacht zijn, maar wel verankerd in je lichaam.", icon: Heart },
              { title: "Duurzame Verandering", desc: "Creëer zowel richting als ruimte voor blijvende transformatie.", icon: Infinity }
            ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3 md:gap-4">
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-[#FAEBEE] rounded-full flex items-center justify-center text-[#854C73] shrink-0">
                        <item.icon size={20} className="md:w-6 md:h-6" />
                      </div>
                      <div>
                        <h4 className="font-serif text-lg md:text-xl text-[#854C73] mb-1 md:mb-2">{item.title}</h4>
                        <p className="text-[#6b4912] text-sm md:text-base leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Highlight: Leergang systemisch leiderschap */}
      <section className="py-16 md:py-24 px-6 relative bg-white overflow-hidden">
        <GrainTexture opacity={0.08} />
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-[#FAEBEE] rounded-full blur-3xl opacity-50 -z-0" aria-hidden="true"></div>

        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="grid md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-7">
              <Badge className="mb-4 bg-[#FAEBEE] text-[#854C73] border-0 px-4 py-1.5 text-sm font-bold uppercase tracking-wide">
                Najaar 2026 · Amsterdam
              </Badge>

              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#854C73] mb-4 leading-tight">
                Leergang <span className="italic font-light font-serif">systemisch leiderschap</span>
              </h2>

              <p className="text-lg md:text-xl text-[#6b4912] mb-6 font-serif italic">
                Duiken in de Onderstroom
              </p>

              <p className="text-[#6b4912] text-base md:text-lg leading-relaxed mb-6">
                Compacte 3-daagse <strong>leergang systemisch leiderschap</strong> voor leiders en adviseurs die de
                onderstroom willen herkennen en daar concreet mee willen werken in hun rol. Praktisch,
                in een kleine groep van maximaal 8 deelnemers.
              </p>

              <ul className="space-y-3 mb-8 text-[#6b4912]">
                <li className="flex items-start gap-3">
                  <Calendar size={18} className="text-[#854C73] mt-1 shrink-0" aria-hidden="true" />
                  <span>3 dagen · 29 en 30 oktober + 27 november 2026</span>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin size={18} className="text-[#854C73] mt-1 shrink-0" aria-hidden="true" />
                  <span>Stadsoase naar Zee, Amsterdam (IJburg)</span>
                </li>
                <li className="flex items-start gap-3">
                  <Users size={18} className="text-[#854C73] mt-1 shrink-0" aria-hidden="true" />
                  <span>Max. 8 deelnemers · €1.500 excl. btw · intake &amp; eindcoaching inbegrepen</span>
                </li>
              </ul>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild size="lg" className="bg-[#854C73] hover:bg-[#590D0D] text-white">
                  <Link to="/leergang-systemisch-leiderschap" title="Leergang systemisch leiderschap — Duiken in de Onderstroom">
                    Bekijk de leergang systemisch leiderschap
                    <ArrowRight className="ml-2 w-4 h-4" aria-hidden="true" />
                  </Link>
                </Button>
              </div>
            </div>

            <div className="md:col-span-5">
              <div className="relative bg-[#854C73] rounded-3xl p-8 md:p-10 text-white shadow-2xl overflow-hidden">
                <GrainTexture opacity={0.15} />
                <div className="absolute -top-12 -right-12 w-48 h-48 border border-white/10 rounded-full opacity-40 pointer-events-none" aria-hidden="true"></div>

                <div className="relative z-10">
                  <GraduationCap size={36} className="mb-6 opacity-80" strokeWidth={1.5} aria-hidden="true" />

                  <h3 className="font-serif text-2xl md:text-3xl mb-4">
                    Wat je leert
                  </h3>

                  <ul className="space-y-3 text-white/80">
                    <li className="flex items-start gap-3">
                      <span className="w-1.5 h-1.5 bg-white rounded-full mt-2.5 shrink-0" aria-hidden="true"></span>
                      <span>Belangrijkste systeemdynamieken herkennen</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-1.5 h-1.5 bg-white rounded-full mt-2.5 shrink-0" aria-hidden="true"></span>
                      <span>Je eigen systemische patronen doorgronden</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-1.5 h-1.5 bg-white rounded-full mt-2.5 shrink-0" aria-hidden="true"></span>
                      <span>Praktisch handelingsperspectief voor casuïstiek</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-1.5 h-1.5 bg-white rounded-full mt-2.5 shrink-0" aria-hidden="true"></span>
                      <span>Eigen organisatieopstelling met diepe inzichten</span>
                    </li>
                  </ul>

                  <p className="mt-6 pt-6 border-t border-white/10 text-sm text-white/60 italic font-serif">
                    "9/10 — De leergang heeft mij meer bewustzijn, taal en lef gegeven."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Review Sectie - Compacte versie, met subtiel sfeerbeeld erboven */}
      <div className="bg-[#FAEBEE]">
        <div className="px-4 sm:px-6 pt-12 sm:pt-16">
          <div className="max-w-xl mx-auto">
            <div className="group rounded-2xl overflow-hidden shadow-md">
              <div className="aspect-[21/9] overflow-hidden">
                <OptimizedImage
                  src={reviewsVisual}
                  webpSrc={reviewsVisual}
                  alt="Sfeerbeeld van een systemische sessie bij Systemique"
                  className="w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                  width={1200}
                  height={515}
                />
              </div>
            </div>
          </div>
        </div>

        <ReviewSection 
          reviews={familieopstellingReviews} 
          compact={true}
          showStructuredData={false}
        />
      </div>

      {/* FAQ Section */}
      <section className="py-24 px-6 bg-white relative">
        <GrainTexture opacity={0.08} />
        <div className="max-w-4xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-3 mb-4">
              <HelpCircle className="w-8 h-8 text-[#854C73]" />
              <Badge variant="outline" className="text-[#854C73] border-[#854C73]">Veelgestelde vragen</Badge>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl text-[#854C73] mb-4">
              Vragen over <span className="italic font-light font-serif opacity-70">systemisch werk</span>
            </h2>
            <p className="text-[#6b4912] text-lg max-w-2xl mx-auto">
              Hieronder vind je antwoorden op de meest gestelde vragen. Staat je vraag er niet bij? Neem gerust contact op.
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="border border-[#FAEBEE] rounded-xl px-6 bg-[#FAEBEE]/30 hover:bg-[#FAEBEE]/50 transition-colors">
              <AccordionTrigger className="text-left text-[#854C73] font-serif text-lg md:text-xl hover:no-underline">
                Wat is systemisch werk?
              </AccordionTrigger>
              <AccordionContent className="text-[#6b4912] leading-relaxed pt-2">
                Systemisch werk kijkt naar de <strong>onderstroom</strong>: de onzichtbare patronen en dynamieken in families, teams of organisaties. Door middel van opstellingen met representanten of symbolen maken we zichtbaar waar gedrag en problemen vandaan komen. Niet alleen de symptomen, maar de <strong>bron</strong>. Dit geeft vaak razendsnel inzicht en creëert ruimte voor duurzame verandering.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="border border-[#FAEBEE] rounded-xl px-6 bg-[#FAEBEE]/30 hover:bg-[#FAEBEE]/50 transition-colors">
              <AccordionTrigger className="text-left text-[#854C73] font-serif text-lg md:text-xl hover:no-underline">
                Wat kost een familieopstelling?
              </AccordionTrigger>
              <AccordionContent className="text-[#6b4912] leading-relaxed pt-2">
                <strong>€100</strong> als vraaginbrenger per opstellingenmiddag. Als representant <strong>€75</strong> per los ticket, of een <Link to="/koop-strippenkaart" className="text-[#854C73] underline hover:text-[#590D0D]">strippenkaart</Link> voor korting bij meerdere sessies.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="border border-[#FAEBEE] rounded-xl px-6 bg-[#FAEBEE]/30 hover:bg-[#FAEBEE]/50 transition-colors">
              <AccordionTrigger className="text-left text-[#854C73] font-serif text-lg md:text-xl hover:no-underline">
                Wat kost een organisatieopstelling?
              </AccordionTrigger>
              <AccordionContent className="text-[#6b4912] leading-relaxed pt-2">
                Dat is maatwerk. Op basis van jouw vraagstuk ontvang je een passende offerte.{' '}
                <Link to="/contact" className="text-[#854C73] underline hover:text-[#590D0D]">
                  Neem contact op
                </Link>{' '}
                via 06-12991453 of <EmailLink className="text-[#854C73] underline hover:text-[#590D0D]" /> voor een vrijblijvend gesprek.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="border border-[#FAEBEE] rounded-xl px-6 bg-[#FAEBEE]/30 hover:bg-[#FAEBEE]/50 transition-colors">
              <AccordionTrigger className="text-left text-[#854C73] font-serif text-lg md:text-xl hover:no-underline">
                Waar vinden de opstellingenmiddagen plaats?
              </AccordionTrigger>
              <AccordionContent className="text-[#6b4912] leading-relaxed pt-2">
                Dat verschilt: van Castricum tot Amsterdam en ook op jouw locatie indien gewenst. Groepsopstellingen vinden vaak plaats in <strong>Het Atelier in Uitgeest</strong>. Bekijk de <Link to="/agenda" className="text-[#854C73] underline hover:text-[#590D0D]">agenda</Link> voor specifieke locaties per datum.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5" className="border border-[#FAEBEE] rounded-xl px-6 bg-[#FAEBEE]/30 hover:bg-[#FAEBEE]/50 transition-colors">
              <AccordionTrigger className="text-left text-[#854C73] font-serif text-lg md:text-xl hover:no-underline">
                Wat kan ik verwachten bij mijn eerste opstelling?
              </AccordionTrigger>
              <AccordionContent className="text-[#6b4912] leading-relaxed pt-2">
                Elke opstelling is anders. Ik zorg ervoor dat <strong>jij regie hebt</strong> op het proces en dat je in een <strong>veilige setting</strong> bent. Je bepaalt zelf hoe diep je gaat en wat je deelt.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-6" className="border border-[#FAEBEE] rounded-xl px-6 bg-[#FAEBEE]/30 hover:bg-[#FAEBEE]/50 transition-colors">
              <AccordionTrigger className="text-left text-[#854C73] font-serif text-lg md:text-xl hover:no-underline">
                Moet ik mijn verhaal delen met de hele groep?
              </AccordionTrigger>
              <AccordionContent className="text-[#6b4912] leading-relaxed pt-2">
                Nee. Als je in een groep komt, vraag ik kort uit wat het vraagstuk is. Je hoeft <strong>geen gedetailleerd verhaal</strong> uit de doeken te doen. Mocht je dit bezwaarlijk vinden, dan kun je ook een <Link to="/1-op-1" className="text-[#854C73] underline hover:text-[#590D0D]">1-op-1 sessie</Link> boeken.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-7" className="border border-[#FAEBEE] rounded-xl px-6 bg-[#FAEBEE]/30 hover:bg-[#FAEBEE]/50 transition-colors">
              <AccordionTrigger className="text-left text-[#854C73] font-serif text-lg md:text-xl hover:no-underline">
                Kan ik ook als representant meedoen zonder eigen vraag?
              </AccordionTrigger>
              <AccordionContent className="text-[#6b4912] leading-relaxed pt-2">
                Ja, er is altijd plek voor representanten. Ik bied ook <Link to="/koop-strippenkaart" className="text-[#854C73] underline hover:text-[#590D0D]">strippenkaarten</Link> aan voor representanten, dat is voordelig als je vaker wilt komen representeren.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-8" className="border border-[#FAEBEE] rounded-xl px-6 bg-[#FAEBEE]/30 hover:bg-[#FAEBEE]/50 transition-colors">
              <AccordionTrigger className="text-left text-[#854C73] font-serif text-lg md:text-xl hover:no-underline">
                Hoe boek ik een kennismakingsgesprek?
              </AccordionTrigger>
              <AccordionContent className="text-[#6b4912] leading-relaxed pt-2">
                Dat kan altijd. Bel me op <a href="tel:+31612991453" className="text-[#854C73] underline hover:text-[#590D0D]">06-12991453</a> of stuur een bericht via het <Link to="/contact" className="text-[#854C73] underline hover:text-[#590D0D]">contactformulier</Link>.
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <div className="text-center mt-12">
            <p className="text-[#6b4912] mb-6">Nog andere vragen?</p>
            <Button asChild variant="outline" size="lg" className="border-[#854C73] text-[#854C73] hover:bg-[#854C73] hover:text-white">
              <Link to="/contact">Stel je vraag</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section - simplified for now, can be enhanced later */}
      <section className="relative py-24 overflow-hidden" style={{ backgroundColor: '#efe8dc' }}>
        <GrainTexture opacity={0.15} />
        <div className="container mx-auto px-6 text-center relative z-10">
          <div className="max-w-3xl mx-auto animate-fade-up">
            <h2 className="font-serif text-4xl md:text-5xl mb-6" style={{ color: '#6b4912' }}>Klaar voor <span className="italic font-light font-serif">duurzame</span> verandering?</h2>
            <p className="text-lg mb-8 leading-relaxed" style={{ color: '#6b4912' }}>
              Laten we kennismaken en ontdekken hoe systemisch werk jouw organisatie of team kan ondersteunen.
            </p>
            <div className="flex flex-col md:flex-row gap-4 justify-center">
              <Button asChild variant="white" size="lg" className="bg-white" style={{ color: '#854C73' }}>
                <Link to="/contact?onderwerp=kennismakingsgesprek">Plan een kennismakingsgesprek</Link>
              </Button>
              <Button asChild variant="outline" size="lg" style={{ borderColor: '#854C73', color: '#6b4912' }} className="hover:bg-[#854C73]/10">
                <Link to="/aanbod">Bekijk het aanbod</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Index;
